import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/apiService';

const emptyForm = { transferType: '', name: '', phone: '', amount: '', note: '', getwayFee: '', gst: '' };

const DirectPayment = () => {
  const { user } = useAuth();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [recordsPerPage] = useState(10);
  const [searchInput, setSearchInput] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const searchDebounceRef = useRef(null);

  // modal state — editingId null = create, set = edit
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);

  // image modal
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');

  const isAdmin = user?.role === 'admin';

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.post('directPaymentList', {
        start: (currentPage - 1) * recordsPerPage,
        length: recordsPerPage,
        search: searchKeyword,
        status: statusFilter
      });
      if (response?.data) {
        setPayments(response.data);
        setTotalRecords(response.recordsTotal || 0);
      }
    } catch {
      toast.error('Failed to load direct payments');
    } finally {
      setLoading(false);
    }
  }, [currentPage, recordsPerPage, searchKeyword, statusFilter]);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  const handleSearchChange = (value) => {
    setSearchInput(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchKeyword(value);
      setCurrentPage(1);
    }, 500);
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (p) => {
    setEditingId(p._id);
    setForm({
      transferType: p.transferType || '',
      name: p.name || '',
      phone: p.phone || '',
      amount: p.amount || '',
      note: p.note || '',
      getwayFee: p.getwayFee || '',
      gst: p.gst || ''
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.transferType || !form.name || !form.phone || !form.amount) {
      toast.error('Transfer type, name, phone and amount are required');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, amount: parseFloat(form.amount) };
      let response;
      if (editingId) {
        response = await apiService.put(`directPayment/${editingId}`, payload);
      } else {
        response = await apiService.post('createDirectPayment', payload);
      }
      if (response.status) {
        toast.success(editingId ? 'Updated successfully' : 'Created successfully');
        setShowModal(false);
        fetchPayments();
      } else {
        toast.error(response.message || 'Failed to save');
      }
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      const response = await apiService.delete(`directPayment/${id}`);
      if (response.status) {
        toast.success('Deleted successfully');
        fetchPayments();
      } else {
        toast.error(response.message || 'Failed to delete');
      }
    } catch {
      toast.error('Failed to delete');
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const response = await apiService.put(`directPaymentStatus/${id}`, { status });
      if (response.status) {
        toast.success('Status updated');
        fetchPayments();
      }
    } catch {
      toast.error('Failed to update status');
    }
  };

  // createdBy can be a populated object {_id,...} or a raw ObjectId string
  const isCreator = (p) => {
    if (!p.createdBy || !user) return false;
    const createdById = p.createdBy?._id?.toString() || p.createdBy?.toString();
    const userId = user._id?.toString() || user.id?.toString();
    return createdById === userId;
  };

  const totalPages = Math.ceil(totalRecords / recordsPerPage);

  const statusBadge = (status) => {
    const map = { pending: 'bg-yellow-100 text-yellow-700', verified: 'bg-green-100 text-green-700', rejected: 'bg-red-100 text-red-700' };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${map[status] || 'bg-gray-100 text-gray-600'}`}>{status}</span>;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-0">
        <div className="bg-white rounded-lg shadow overflow-hidden">

          {/* Header */}
          <div className="bg-gray-50 p-6 border-b border-gray-200">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
              <h1 className="text-2xl font-bold text-gray-900">Direct Payments</h1>
              {isAdmin && (
                <button
                  onClick={openCreate}
                  className="flex items-center gap-2 px-4 py-2 bg-[#009ef7] text-white rounded-lg hover:bg-[#0086d3] text-sm font-medium"
                >
                  <i className="bi bi-plus-lg"></i> Add Entry
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-4 items-center">
              <div className="relative w-64">
                <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                <input
                  type="text"
                  placeholder="Search by name, phone..."
                  value={searchInput}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7]"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="verified">Verified</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name / Phone</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Transfer Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fee / GST</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Proofs</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Note</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created By</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  {isAdmin && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr><td colSpan={isAdmin ? 11 : 10} className="px-6 py-8 text-center">
                    <div className="flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#009ef7]"></div></div>
                  </td></tr>
                ) : payments.length === 0 ? (
                  <tr><td colSpan={isAdmin ? 11 : 10} className="px-6 py-8 text-center text-gray-500">No direct payments found</td></tr>
                ) : (
                  payments.map((p, i) => (
                    <tr key={p._id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 text-gray-500 text-xs">{(currentPage - 1) * recordsPerPage + i + 1}</td>
                      <td className="px-4 py-4">
                        <div className="font-medium text-gray-900">{p.name}</div>
                        <div className="text-xs text-gray-500">{p.phone}</div>
                      </td>
                      <td className="px-4 py-4 text-gray-700">{p.transferType}</td>
                      <td className="px-4 py-4 font-semibold text-gray-900">₹{p.amount?.toLocaleString()}</td>
                      <td className="px-4 py-4 text-xs text-gray-500">
                        {p.getwayFee && <div>Fee: ₹{p.getwayFee}</div>}
                        {p.gst && <div>GST: ₹{p.gst}</div>}
                        {!p.getwayFee && !p.gst && <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-4">
                        {p.proofs?.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {p.proofs.map((proof, idx) => (
                              <button key={idx}
                                onClick={() => { setSelectedImage(`${process.env.REACT_APP_MEDIA_URL}${proof}`); setShowImageModal(true); }}
                                className="px-2 py-1 text-xs bg-[#009ef7]/10 text-blue-700 rounded hover:bg-blue-200"
                              >Proof {idx + 1}</button>
                            ))}
                          </div>
                        ) : <span className="text-gray-300 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-4 text-xs text-gray-500 max-w-[120px] truncate">{p.note || '—'}</td>
                      <td className="px-4 py-4 text-xs text-gray-500">
                        {p.createdBy?.first_name
                          ? `${p.createdBy.first_name} ${p.createdBy.last_name || ''}`.trim()
                          : p.createdBy ? 'Admin' : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-col gap-1">
                          {statusBadge(p.status)}
                          <select
                            value={p.status}
                            onChange={(e) => updateStatus(p._id, e.target.value)}
                            className="mt-1 text-xs border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-[#009ef7]"
                          >
                            <option value="pending">Pending</option>
                            <option value="verified">Verified</option>
                            <option value="rejected">Rejected</option>
                          </select>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-xs text-gray-500 whitespace-nowrap">
                        {new Date(p.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                      </td>
                      {isAdmin && (
                        <td className="px-4 py-4">
                          {isCreator(p) ? (
                            <div className="flex gap-2">
                              <button
                                onClick={() => openEdit(p)}
                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                                title="Edit"
                              ><i className="bi bi-pencil text-xs"></i></button>
                              <button
                                onClick={() => handleDelete(p._id)}
                                className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                                title="Delete"
                              ><i className="bi bi-trash text-xs"></i></button>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-300">—</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-6 py-3 border-t border-gray-200 flex items-center justify-between">
              <span className="text-sm text-gray-700">
                Showing {((currentPage - 1) * recordsPerPage) + 1} to {Math.min(currentPage * recordsPerPage, totalRecords)} of {totalRecords} results
              </span>
              <div className="flex items-center space-x-2">
                <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1}
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50">Previous</button>
                <span className="text-sm text-gray-700">Page {currentPage} of {totalPages}</span>
                <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50">Next</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{editingId ? 'Edit Entry' : 'Add Direct Payment Entry'}</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700"><i className="bi bi-x-lg"></i></button>
            </div>
            <form onSubmit={handleSave} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Transfer Type *</label>
                  <input name="transferType" value={form.transferType}
                    onChange={(e) => setForm(f => ({ ...f, transferType: e.target.value }))}
                    placeholder="e.g. NEFT, UPI, IMPS"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]" required />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Amount (₹) *</label>
                  <input name="amount" type="number" min="0" step="0.01" value={form.amount}
                    onChange={(e) => setForm(f => ({ ...f, amount: e.target.value }))}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]" required />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Name *</label>
                  <input name="name" value={form.name}
                    onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="Payer name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]" required />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Phone *</label>
                  <input name="phone" value={form.phone}
                    onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
                    placeholder="Phone number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]" required />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Gateway Fee</label>
                  <input name="getwayFee" type="number" min="0" step="0.01" value={form.getwayFee}
                    onChange={(e) => setForm(f => ({ ...f, getwayFee: e.target.value }))}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">GST</label>
                  <input name="gst" type="number" min="0" step="0.01" value={form.gst}
                    onChange={(e) => setForm(f => ({ ...f, gst: e.target.value }))}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Note</label>
                <textarea name="note" value={form.note}
                  onChange={(e) => setForm(f => ({ ...f, note: e.target.value }))}
                  placeholder="Optional note..." rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]" />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
                <button type="submit" disabled={saving}
                  className="px-4 py-2 text-sm text-white bg-[#009ef7] rounded-lg hover:bg-[#0086d3] disabled:opacity-50">
                  {saving ? 'Saving...' : editingId ? 'Update Entry' : 'Create Entry'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {showImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 max-w-2xl max-h-[80vh] overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Payment Proof</h3>
              <button onClick={() => setShowImageModal(false)} className="text-gray-500 hover:text-gray-700"><i className="bi bi-x-lg"></i></button>
            </div>
            <img src={selectedImage} alt="Payment Proof" className="max-w-full h-auto rounded-lg"
              onError={(e) => { e.target.src = '/placeholder-image.png'; }} />
          </div>
        </div>
      )}
    </div>
  );
};

export default DirectPayment;
